import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { MdOutlineZoomIn, MdOutlineZoomOut } from 'react-icons/md'
import {
    COLORS,
    closestNumbers,
    drawEdges,
    getCoords,
    getEdges,
    getMetesAndBounds,
    setShadow,
    withinBounds,
} from './canvasUtils'
import useEffectOnce from '@/hook/useEffectOnce'

const SCALES = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
const SIZES = [   6,   8,   9,  10,  11,  12,  12,  12,  14, 14]

const PlotterCanvas = forwardRef(({ plots, updatePlot, activePlot }, ref) => {
    
    // const [hasEdits, setHasEdits] = useState(false)
    // const originalCoords = geoJSON.features.map(f => f.geometry.coordinates).flat()
    // const pointsRef = useRef([])
    
    const canvasRef = useRef(null)
    const coordinates = useRef([])
    const squareRef = useRef({ minX: 0, minY: 0, maxX: 0, maxY: 0 })
    const moveOffsetRef = useRef({ x: 0, y: 0 })
    const [scale, setScale] = useState(1)
    
    const clearRect = () => {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        
        ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    
    const hasPlotChanges = () => {
        if (activePlot !== null) {
            const originalCoords = getCoords(plots[activePlot])
            const currentCoords = coordinates.current[activePlot].coords
            
            return originalCoords.some((ogCoord, idx) =>
                currentCoords[idx][0] !== ogCoord[0]
                || currentCoords[idx][1] !== ogCoord[1])
        }
        
        return false
    }
    
    const drawTexts = metesAndBounds => {
        if (!metesAndBounds) return
        /** @type {HTMLCanvasElement} */
        const canvas = canvasRef.current
        const moveOffset = moveOffsetRef.current
        const ctx = canvas.getContext('2d')
        
        metesAndBounds.forEach(mete => {
            
            const scaledStartingPoint = mete.startingPoint
                .map((p, i) => p * scale + moveOffset[i % 2 ? 'y' : 'x'])
            const scaledEndingPoint = mete.endingPoint
                .map((p, i) => p * scale + moveOffset[i % 2 ? 'y' : 'x'])
            const middlePoint = [(scaledStartingPoint[0] + scaledEndingPoint[0]) / 2,
                (scaledStartingPoint[1] + scaledEndingPoint[1]) / 2]
            
            let radian = Math.atan2(mete.endingPoint[1] - mete.startingPoint[1],
                mete.endingPoint[0] - mete.startingPoint[0])
            let textOffset = 15
            
            if (Math.abs(radian) > (Math.PI / 2)) {
                radian = radian - Math.PI
                textOffset = -10
            }
            
            const scaleIdx = SCALES.indexOf(scale)
            
            ctx.save()
            ctx.font = `${SIZES[scaleIdx] || 10}px Arial`
            const textSize = ctx.measureText(mete.text)
            
            ctx.moveTo(0, 0)
            ctx.translate(...middlePoint)
            ctx.rotate(radian)
            ctx.textAlign = 'center'
            ctx.fillStyle = 'white'
            ctx.fillText(mete.text, 0, textOffset, textSize.width)
            ctx.restore()
        })
    }
    
    const drawPolygon = (coords, plotColor, opts = { highlight: false, opacity: false, draggable: false }) => {
        // const points = pointsRef.current
        const canvas = canvasRef.current
        const moveOffset = moveOffsetRef.current
        const ctx = canvas.getContext('2d')
        const cds = coords.map(c => [c[0] * scale + moveOffset.x, c[1] * scale + moveOffset.y])
        
        ctx.beginPath()
        ctx.lineWidth = 3
        ctx.lineJoin = 'round'
        ctx.strokeStyle = plotColor + (opts.opacity && !opts.highlight ? '10' : '')
        setShadow(ctx, opts.highlight && { shadowColor: plotColor, shadowBlur: 8 })
        
        cds.forEach(point => ctx.lineTo(point[0], point[1]))
        ctx.stroke()
        
        if (opts.draggable) {
            
            const square = squareRef.current
            const edges = getEdges(cds)
            
            ctx.lineWidth = 1
            ctx.strokeStyle = '#FFFFFFAA'
            setShadow(ctx)
            ctx.beginPath()
            drawEdges(ctx, edges)
            
            Object.assign(square, edges)
            
            // @todo single point dragging (disabled for now)
            // ctx.fillStyle = invertHex(plotColor)
            // cds.forEach((point, idx) => {
            //     const x = point[0] - BULLET_SIZE / 2
            //     const y = point[1] - BULLET_SIZE / 2
            //     points[idx] = { ...points[idx], x, y }
            //     ctx.fillRect(x, y, BULLET_SIZE, BULLET_SIZE)
            // })
        }
    }
    
    const canvasDragOn = e => {
        const { offsetX, offsetY } = e
        const square = squareRef.current
        const within = withinBounds({ offsetX, offsetY }, square)
        
        if (e.buttons === 1 && (activePlot === null || !within)) {
            canvasRef.current.style.cursor = 'move'
            return
        }
        
        if (within && e.buttons === 1) {
            square.active = true
            canvasRef.current.style.cursor = 'grabbing'
        }
        
        // @todo single point dragging (disabled for now)
        // const points = pointsRef.current
        
        // const pointWithinBounds = points.find(({ x, y }) =>
        //     withinBounds({ offsetX, offsetY },
        //    { minX: x, maxX: x + BULLET_SIZE, minY: y, maxY: y + BULLET_SIZE}, 10))
        
        // if (pointWithinBounds)
        //     pointWithinBounds.dragging = true
    }
    
    const canvasDragging = e => {
        const { offsetX, offsetY, movementX, movementY } = e
        const moveOffset = moveOffsetRef.current
        const square = squareRef.current
        const squareWithinBounds = withinBounds({ offsetX, offsetY }, square)
        
        if (e.buttons !== 1) {
            if (activePlot !== null && squareWithinBounds)
                canvasRef.current.style.cursor = 'grab'
            else if (!squareWithinBounds)
                canvasRef.current.style.cursor = 'default'
        }
        
        if (activePlot !== null && square.active) {
            
            coordinates.current[activePlot].coords.forEach(coords => {
                coords[0] += movementX / scale
                coords[1] += movementY / scale
            })
            
            clearRect()
            
            coordinates.current.forEach(({ coords, hidden }, idx) => {
                if (!hidden)
                    drawPolygon(coords, COLORS[idx], { opacity: activePlot !== null })
            })
            
            drawPolygon(coordinates.current[activePlot].coords, COLORS[activePlot],
                { highlight: true, draggable: true })
            return
            
        }
        
        if (e.buttons === 1) {
            moveOffset.x += movementX
            moveOffset.y += movementY
            
            clearRect()
            coordinates.current.forEach(({ coords, hidden }, idx) => {
                if (!hidden)
                    drawPolygon(coords, COLORS[idx], { opacity: activePlot !== null })
            })
            
            if (activePlot !== null) {
                
                drawPolygon(coordinates.current[activePlot].coords, COLORS[activePlot],
                    { highlight: true, draggable: true })
            }
            
            return
        }
        
        // @todo single point dragging (disabled for now)
        // const points = pointsRef.current
        // const idx = points.findIndex(p => p.dragging)
        
        // if (activePlot !== null && idx > -1) {
        //     coordinates.current[activePlot][idx] = [(offsetX - moveOffset.x) / scale,
        // (offsetY - moveOffset.y) / scale]
        //     // setHasEdits(true)
        //     clearRect()
        
        //     coordinates.current.forEach((coords, idx) => {
        //         drawPolygon(coords, COLORS[idx], { opacity: true })
        //     })
        //     drawPolygon(coordinates.current[activePlot], COLORS[activePlot], { highlight: true, draggable: true })
        // }
    }
    
    const canvasDragOff = () => {
        canvasRef.current.style.cursor = 'default'
        const square = squareRef.current
        
        square.active = false
        
        if (activePlot !== null) {
            clearRect()
            coordinates.current.forEach(({ coords, hidden }, idx) => {
                if (!hidden)
                    drawPolygon(coords, COLORS[idx], { opacity: activePlot !== null })
            })
            drawPolygon(coordinates.current[activePlot].coords, COLORS[activePlot],
                { highlight: true, draggable: true })
            drawTexts(coordinates.current[activePlot].metesAndBounds)
        }
        
        // single point dragging (disabled for now)
        // pointsRef.current.forEach(p => p.dragging = false)
        if (hasPlotChanges()) {
            updatePlot(coordinates.current[activePlot].coords)
        }
    }
    
    const canvasWheel = e => {
        e.preventDefault()
        setScale(scale => {
            const [lower, upper] = closestNumbers(scale, SCALES)
            
            return e.deltaY > 0 ? lower : upper
        })
    }
    
    useEffect(() => {
        if (canvasRef.current) {
            const { width } = canvasRef.current.parentElement.getBoundingClientRect()
            
            canvasRef.current.width = width
            canvasRef.current.height = width
            
            coordinates.current.length = 0
            plots.forEach((p, idx) => {
                const coords = getCoords(p)
                const metesAndBounds = getMetesAndBounds(p)
                
                coordinates.current.push({ hidden: p.hidden, coords, metesAndBounds })
                if (!p.hidden)
                    drawPolygon(coords, COLORS[idx], { opacity: activePlot !== null })
            })
            
            if (activePlot !== null) {
                const coords = getCoords(plots[activePlot])
                const metesAndBounds = getMetesAndBounds(plots[activePlot])
                
                drawPolygon(coords, COLORS[activePlot], { highlight: true, draggable: true })
                drawTexts(metesAndBounds)
            }
            
        }
        
    }, [plots, canvasRef, activePlot, scale])
    
    useEffect(() => {
        if (canvasRef.current) {
            canvasRef.current.addEventListener('mousedown', canvasDragOn)
            canvasRef.current.addEventListener('mouseup', canvasDragOff)
            // canvasRef.current.addEventListener('mouseenter', canvasMouseEnter)
            canvasRef.current.addEventListener('mouseleave', canvasDragOff)
            canvasRef.current.addEventListener('mousemove', canvasDragging)
            canvasRef.current.addEventListener('wheel', canvasWheel)
        }
        
        return () => {
            canvasRef.current?.removeEventListener('mousedown', canvasDragOn)
            canvasRef.current?.removeEventListener('mouseup', canvasDragOff)
            // canvasRef.current?.removeEventListener('mouseenter', canvasMouseEnter)
            canvasRef.current?.removeEventListener('mouseleave', canvasDragOff)
            canvasRef.current?.removeEventListener('mousemove', canvasDragging)
            canvasRef.current?.removeEventListener('wheel', canvasWheel)
        }
    }, [canvasRef, scale, activePlot])
    
    useEffectOnce(() => {
        
        if (!plots.length) return false
        
        const coords = plots.map(p => getCoords(p)).flat()
        const moveOffset = moveOffsetRef.current
        const { minX, maxX, minY, maxY } = getEdges(coords)
        const x = {
            min: Math.abs(minX),
            max: Math.abs(maxX),
        }
        const y = {
            min: Math.abs(minY),
            max: Math.abs(maxY),
        }
        const { width } = canvasRef.current.parentElement.getBoundingClientRect()
        const scaleX = 1 / ((x.min + x.max) / width)
        const scaleY = 1 / ((y.min + y.max) / width)
        const initialScale = Math.min(scaleX, scaleY) * 0.95
        
        const [, upper] = closestNumbers(initialScale, SCALES)
        
        setScale(upper)
        moveOffset.x = x.min * 1.025 * upper
        moveOffset.y = y.min * 1.025 * upper
        
        return true
        
    }, [plots])
    
    const exportCanvas = () => {
        const canvas = canvasRef.current
        // eslint-disable-next-line no-restricted-globals
        const link = document.createElement('a')
        
        link.download = 'Deed Plotter.png'
        
        clearRect()
        coordinates.current.forEach(({ coords, hidden }, idx) => {
            if (!hidden)
                drawPolygon(coords, COLORS[idx])
        })
        
        link.href = canvas.toDataURL()
        
        clearRect()
        coordinates.current.forEach(({ coords, hidden }, idx) => {
            if (!hidden)
                drawPolygon(coords, COLORS[idx], { opacity: activePlot !== null })
        })
        if (activePlot !== null)
            drawPolygon(coordinates.current[activePlot].coords, COLORS[activePlot],
                { highlight: true, draggable: true })
        link.click()
    }
    
    useImperativeHandle(ref, () => ({ exportCanvas }))
    
    return (
        <div className="PlotterCanvas">
            <div className="actions-wrapper">
                <button
                    className="canvas-btn"
                    onClick={() => setScale(scale => closestNumbers(scale, SCALES)[1])}>
                    <MdOutlineZoomIn />
                </button>
                <button
                    className="canvas-btn"
                    onClick={() => setScale(scale => closestNumbers(scale, SCALES)[0])}>
                    <MdOutlineZoomOut />
                </button>
            </div>
            <canvas ref={canvasRef}></canvas>
        </div>
    )
})

PlotterCanvas.displayName = 'PlotterCanvas'

export default PlotterCanvas
